import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Mail, Lock, Loader2, ShieldCheck } from 'lucide-react';

const PortalLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/portal';

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      nav(from, { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-slate-50">
      <div
        className="hidden md:flex flex-col justify-between p-10 text-white"
        style={{ background: 'linear-gradient(135deg, #0f3d78 0%, #1e73be 60%, #2ea690 100%)' }}
      >
        <img
          src="https://customer-assets.emergentagent.com/job_site-creator-3034/artifacts/pbfdilsv_Finirio.JPG"
          alt="Finirio"
          className="h-11 w-auto bg-white p-2 rounded-md self-start"
        />
        <div>
          <h1 className="font-heading text-4xl font-extrabold leading-tight">Finirio Internal Portal</h1>
          <p className="mt-3 text-white/85 max-w-md">
            Central hub for our team &mdash; policies, product SOPs, training material and role-based resources.
          </p>
          <div className="mt-6 flex items-center gap-2 text-white/85 text-sm">
            <ShieldCheck className="w-4 h-4" /> JWT-secured, role-based access
          </div>
        </div>
        <p className="text-white/70 text-sm">&copy; 2026 Finirio</p>
      </div>
      <div className="grid place-items-center p-6">
        <div className="w-full max-w-[420px] bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Sign in</h2>
          <p className="text-slate-600 text-sm mt-1">Access your Finirio portal account</p>
          <form className="mt-6 space-y-4" onSubmit={submit}>
            <FieldInput icon={Mail} label="Email" type="email" value={email} onChange={setEmail} placeholder="you@finirio.com" required />
            <FieldInput icon={Lock} label="Password" type="password" value={password} onChange={setPassword} placeholder="Your password" required />
            {err && <p className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-md text-white font-semibold hover:brightness-110 transition disabled:opacity-70 inline-flex items-center justify-center gap-2"
              style={{ backgroundColor: '#1e73be' }}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
          <div className="mt-5 text-center text-sm text-slate-600">
            New here?{' '}
            <Link to="/portal/register" className="text-[#1e73be] font-semibold hover:underline">
              Request access
            </Link>
          </div>
          <div className="mt-3 text-center">
            <Link to="/" className="text-xs text-slate-500 hover:text-slate-700">&larr; Back to website</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const FieldInput = ({ icon: Icon, label, value, onChange, type = 'text', placeholder, required }) => (
  <div>
    <label className="block text-slate-700 text-[13px] font-semibold mb-1.5">{label}</label>
    <div className="flex items-center border border-slate-200 rounded-md overflow-hidden focus-within:border-[#1e73be] focus-within:ring-2 focus-within:ring-[#1e73be]/20 transition">
      <span className="px-3 h-11 grid place-items-center bg-slate-50 border-r border-slate-200 text-slate-500">
        <Icon className="w-4 h-4" />
      </span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 h-11 px-3 outline-none text-slate-800"
      />
    </div>
  </div>
);

export default PortalLogin;
